import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, ShieldCheck, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { dateOnly } from '@/lib/utils';
import { ABOUT_SETTINGS_PATH } from '@/lib/updates-api';

const NOTICE_KEY = 'his.support-notice.dismissed';

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function dismissedNotice(): string | null {
  try {
    return localStorage.getItem(NOTICE_KEY);
  } catch {
    return null;
  }
}

function rememberDismissed(id: string): void {
  try {
    localStorage.setItem(NOTICE_KEY, id);
  } catch {
    return;
  }
}

export function SupportSessionBanner() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<string | null>(() => dismissedNotice());
  const impersonation = user?.impersonation ?? null;

  useEffect(() => {
    if (!impersonation) return;
    const remaining = new Date(impersonation.endsAt).getTime() - Date.now();
    const timer = window.setTimeout(() => {
      void signOut().then(() => navigate('/impersonate', { replace: true }));
    }, Math.max(0, Math.min(remaining, 2_147_000_000)));
    return () => window.clearTimeout(timer);
  }, [impersonation, signOut, navigate]);

  if (!user || user.isPlatformUser) return null;

  if (impersonation) {
    const live = impersonation.mode === 'live';
    return (
      <div
        role="status"
        className={
          live
            ? 'flex flex-wrap items-center justify-center gap-2 border-b border-destructive/40 bg-destructive/10 px-4 py-2 text-xs font-medium text-destructive no-print'
            : 'flex flex-wrap items-center justify-center gap-2 border-b border-primary/30 bg-primary/10 px-4 py-2 text-xs font-medium text-primary no-print'
        }
      >
        <Eye className="h-3.5 w-3.5 flex-none" aria-hidden />
        <span>
          {live ? 'Owner live session' : 'Owner session'} as {user.displayName}: read-only, no clinical records. Ends at {timeOf(impersonation.endsAt)}.
        </span>
        <button
          type="button"
          className="underline underline-offset-2"
          onClick={() => void signOut().then(() => navigate('/impersonate', { replace: true }))}
        >
          End session
        </button>
      </div>
    );
  }

  const recent = user.isSuperAdmin ? user.recentSupportAccess ?? null : null;
  if (!recent || dismissed === recent.id) return null;
  return (
    <div role="status" className="flex flex-wrap items-center justify-center gap-2 border-b px-4 py-2 text-xs text-muted-foreground no-print">
      <ShieldCheck className="h-3.5 w-3.5 flex-none" aria-hidden />
      <span>
        The platform Owner ({recent.by}) signed in to this facility on {dateOnly(recent.startedAt)} at {timeOf(recent.startedAt)}
        {recent.reason ? ` — ${recent.reason}` : ''}. {recent.active ? 'The session is still open.' : 'The session has ended.'}
      </span>
      <button type="button" className="underline underline-offset-2" onClick={() => navigate(ABOUT_SETTINGS_PATH)}>
        Support access log
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        className="rounded p-0.5 hover:bg-muted"
        onClick={() => {
          rememberDismissed(recent.id);
          setDismissed(recent.id);
        }}
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
